import { useRef, useState, useCallback, useEffect } from "react";
import { Button, Form, Input, Upload, Tooltip } from "antd";
import type { InputRef, UploadProps } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { PaperIcon } from "@components/icons/PaperIcon";
import { ImagePlusIcon } from "@components/icons/ImagePlusIcon";
import { VideoIcon } from "@components/icons/VideoIcon";
import { EmojiIcon } from "@components/icons/EmojiIcon";
import { uploadFacebookAttachment } from "@api/services/facebook";
import { uploadZaloImage } from "@api/services/zalo";
import type { Conversation } from "@interfaces/chat.interface";
import type { MessageTemplate } from "@api/services/facebook";
import { useNotificationContext } from "@hooks/useNotificationContext";
import { CHANNELS } from "@common";
import { AttachmentRenderer } from "./AttachmentRenderer";
import { EmojiPicker } from "./EmojiPicker";
import { TemplatePicker } from "./TemplatePicker";
import styles from "./styles.module.less";
import { TemplateIcon } from "@components/icons/TemplateIcon";

interface AttachmentInfo {
  attachmentId: string;
  type: "image" | "video" | "file";
  fileName: string;
  fileUrl?: string;
}

interface UploadingFile {
  id: string;
  fileName: string;
  type: "image" | "video" | "file";
  fileUrl?: string;
}

interface ChatInputProps {
  onSend: (value: string, attachments?: AttachmentInfo[]) => void;
  pageId?: string | null;
  disabled?: boolean;
  activeConversation?: Conversation;
}

const ZALO_IMAGE_MAX_SIZE = 1024 * 1024;

export const ChatInput = ({
  onSend,
  pageId,
  disabled = false,
  activeConversation,
}: ChatInputProps) => {
  const [form] = Form.useForm();
  const inputRef = useRef<InputRef>(null);
  const [pendingAttachments, setPendingAttachments] = useState<
    AttachmentInfo[]
  >([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);

  const handleFinish = ({ message }: { message: string }) => {
    if (disabled) return;

    const messageText = message?.trim() || "";
    if (!messageText && pendingAttachments.length === 0) return;

    onSend(
      messageText,
      pendingAttachments.length > 0 ? pendingAttachments : undefined
    );
    form.resetFields();
    setPendingAttachments([]);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      form.submit();
    }
  };

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      if (disabled) return;

      const currentValue = form.getFieldValue("message") || "";
      interface TextAreaRef {
        resizableTextArea?: {
          textArea?: HTMLTextAreaElement;
        };
        input?: HTMLInputElement | HTMLTextAreaElement;
      }
      const ref = inputRef.current as TextAreaRef | null;
      const textareaElement =
        ref?.resizableTextArea?.textArea ||
        (ref?.input && "selectionStart" in ref.input
          ? (ref.input as HTMLTextAreaElement)
          : undefined);

      let start = currentValue.length;
      let end = currentValue.length;

      if (textareaElement && "selectionStart" in textareaElement) {
        start = textareaElement.selectionStart || 0;
        end = textareaElement.selectionEnd || 0;
      }

      const newValue =
        currentValue.slice(0, start) + emoji + currentValue.slice(end);
      form.setFieldsValue({ message: newValue });
      setTimeout(() => {
        if (textareaElement) {
          textareaElement.focus();
          const newCursorPos = start + emoji.length;
          textareaElement.setSelectionRange(newCursorPos, newCursorPos);
        } else {
          inputRef.current?.focus();
        }
      }, 10);
    },
    [form, disabled]
  );

  const handleTemplateSelect = useCallback(
    (template: MessageTemplate) => {
      if (disabled) return;

      try {
        const payload = JSON.parse(template.payload || "{}");
        let templateText = "";

        // Xử lý payload của template
        if (payload.title) {
          templateText += payload.title;
        }
        if (payload.subtitle) {
          templateText += templateText
            ? `\n${payload.subtitle}`
            : payload.subtitle;
        }
        if (payload.text) {
          templateText += templateText ? `\n${payload.text}` : payload.text;
        }

        // Nếu không có text, thử lấy từ các field khác
        if (!templateText && payload.elements) {
          // Xử lý generic template với elements
          const firstElement = Array.isArray(payload.elements)
            ? payload.elements[0]
            : null;
          if (firstElement?.title) {
            templateText = firstElement.title;
            if (firstElement.subtitle) {
              templateText += `\n${firstElement.subtitle}`;
            }
          }
        }

        // Nếu vẫn không có text, dùng tên template
        if (!templateText) {
          templateText = template.name;
        }

        const currentValue = form.getFieldValue("message") || "";
        const newValue = currentValue
          ? `${currentValue}\n\n${templateText}`
          : templateText;

        form.setFieldsValue({ message: newValue });
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      } catch (error) {
        console.error("Error parsing template payload:", error);
        // Fallback: chỉ insert tên template
        const currentValue = form.getFieldValue("message") || "";
        const newValue = currentValue
          ? `${currentValue}\n\n${template.name}`
          : template.name;
        form.setFieldsValue({ message: newValue });
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      }
    },
    [form, disabled]
  );

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }, []);

  const loadImageFromFile = useCallback(
    (file: File): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = (event) => {
          URL.revokeObjectURL(url);
          reject(event);
        };
        img.src = url;
      });
    },
    []
  );

  const canvasToBlob = useCallback(
    (
      canvas: HTMLCanvasElement,
      type: string,
      quality: number
    ): Promise<Blob | null> =>
      new Promise((resolve) => canvas.toBlob(resolve, type, quality)),
    []
  );

  const buildCompressedFileName = useCallback(
    (originalName: string, mimeType: string) => {
      const extension =
        mimeType === "image/jpeg"
          ? "jpg"
          : mimeType === "image/webp"
          ? "webp"
          : "png";
      const baseName = originalName.replace(/\.[^.]+$/, "");
      return `${baseName}.${extension}`;
    },
    []
  );

  const compressImageFile = useCallback(
    async (file: File, maxBytes: number): Promise<File> => {
      if (file.size <= maxBytes) return file;

      const image = await loadImageFromFile(file);
      const targetType =
        file.type === "image/jpeg" || file.type === "image/webp"
          ? file.type
          : "image/jpeg";

      let scale = Math.min(1, Math.sqrt(maxBytes / file.size));
      scale = Math.max(scale, 0.3);
      let quality = 0.9;

      const render = async (nextScale: number, nextQuality: number) => {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.floor(image.width * nextScale));
        canvas.height = Math.max(1, Math.floor(image.height * nextScale));
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        return canvasToBlob(canvas, targetType, nextQuality);
      };

      let blob = await render(scale, quality);
      let attempts = 0;

      while (blob && blob.size > maxBytes && attempts < 6) {
        attempts += 1;
        if (quality > 0.6) {
          quality -= 0.1;
        } else {
          scale = Math.max(0.3, scale * 0.85);
          quality = 0.85;
        }
        blob = await render(scale, quality);
      }

      if (!blob) {
        throw new Error("Không thể nén ảnh");
      }

      const compressedName = buildCompressedFileName(file.name, targetType);
      return new File([blob], compressedName, { type: targetType });
    },
    [buildCompressedFileName, canvasToBlob, loadImageFromFile]
  );

  const { success, error: showError } = useNotificationContext();

  const handleFileUpload = useCallback(
    async (file: File, acceptType?: string) => {
      const isZaloConversation = activeConversation?.channel === CHANNELS.ZALO;
      const isFacebookConversation =
        activeConversation?.channel === CHANNELS.FACEBOOK;

      if (isFacebookConversation && !pageId) {
        showError("Không tìm thấy page ID");
        return false;
      }

      if (isZaloConversation) {
        if (!file.type.startsWith("image/")) {
          showError("Zalo chỉ hỗ trợ gửi ảnh");
          return false;
        }

        let fileToUpload = file;
        if (file.size > ZALO_IMAGE_MAX_SIZE) {
          try {
            fileToUpload = await compressImageFile(file, ZALO_IMAGE_MAX_SIZE);
          } catch (error) {
            console.error("Error compressing Zalo image:", error);
            showError("Không thể nén ảnh");
            return false;
          }
        }

        if (fileToUpload.size > ZALO_IMAGE_MAX_SIZE) {
          showError("Không thể nén ảnh xuống dưới 1MB");
          return false;
        }

        const uploadingId = `${Date.now()}-${Math.random()}`;
        let base64Url: string | undefined;

        try {
          base64Url = await fileToBase64(fileToUpload);
        } catch (error) {
          console.error("Error converting image to base64:", error);
        }

        const uploadingFile: UploadingFile = {
          id: uploadingId,
          fileName: fileToUpload.name,
          type: "image",
          fileUrl: base64Url,
        };
        setUploadingFiles((prev) => [...prev, uploadingFile]);

        try {
          const attachmentId = await uploadZaloImage(fileToUpload);
          const newAttachment: AttachmentInfo = {
            attachmentId,
            type: "image",
            fileName: fileToUpload.name,
            fileUrl: base64Url,
          };
          setPendingAttachments((prev) => [...prev, newAttachment]);
          success("Đã tải ảnh thành công");
          return false;
        } catch (error) {
          console.error("Error uploading Zalo image:", error);
          showError("Tải ảnh thất bại");
          return false;
        } finally {
          setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadingId));
        }
      }

      let attachmentType: "image" | "video" | "file" = "file";
      if (acceptType === "image/*" || file.type.startsWith("image/")) {
        attachmentType = "image";
      } else if (acceptType === "video/*" || file.type.startsWith("video/")) {
        attachmentType = "video";
      }

      const uploadingId = `${Date.now()}-${Math.random()}`;

      let base64Url: string | undefined;
      let blobUrl: string | undefined;

      if (file.type.startsWith("image/")) {
        try {
          base64Url = await fileToBase64(file);
        } catch (error) {
          console.error("Error converting image to base64:", error);
        }
      } else if (file.type.startsWith("video/")) {
        try {
          blobUrl = URL.createObjectURL(file);
        } catch (error) {
          console.error("Error creating blob URL for video:", error);
          try {
            base64Url = await fileToBase64(file);
          } catch (base64Error) {
            console.error("Error converting video to base64:", base64Error);
          }
        }
      }

      const previewUrl = blobUrl || base64Url;
      const uploadingFile: UploadingFile = {
        id: uploadingId,
        fileName: file.name,
        type: attachmentType,
        fileUrl: previewUrl,
      };
      setUploadingFiles((prev) => [...prev, uploadingFile]);

      try {
        if (!pageId) {
          showError("Không tìm thấy page ID");
          return false;
        }
        const response = await uploadFacebookAttachment({
          file,
          pageId,
        });

        if (response.code === 0 && response.data?.attachmentId) {
          const previewUrl = blobUrl || base64Url;
          const newAttachment: AttachmentInfo = {
            attachmentId: response.data.attachmentId,
            type: attachmentType,
            fileName: file.name,
            fileUrl: previewUrl,
          };
          setPendingAttachments((prev) => [...prev, newAttachment]);
          success("Đã tải file thành công");
          return false;
        } else {
          showError(response.message || "Tải file thất bại");
          return false;
        }
      } catch (error) {
        console.error("Error uploading attachment:", error);
        showError("Tải file thất bại");
        return false;
      } finally {
        setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadingId));
      }
    },
    [activeConversation, pageId, fileToBase64, showError, success]
  );

  console.log("activeConversation", activeConversation);

  const handleRemoveAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => {
      const attachment = prev[index];
      if (attachment?.fileUrl && attachment.fileUrl.startsWith("blob:")) {
        URL.revokeObjectURL(attachment.fileUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  useEffect(() => {
    return () => {
      pendingAttachments.forEach((attachment) => {
        if (attachment?.fileUrl && attachment.fileUrl.startsWith("blob:")) {
          URL.revokeObjectURL(attachment.fileUrl);
        }
      });
      uploadingFiles.forEach((file) => {
        if (file?.fileUrl && file.fileUrl.startsWith("blob:")) {
          URL.revokeObjectURL(file.fileUrl);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isZaloConversation = activeConversation?.channel === CHANNELS.ZALO;
  const fileUploadProps: UploadProps = {
    multiple: true,
    showUploadList: false,
    beforeUpload: (file) => handleFileUpload(file),
  };

  const imageUploadProps: UploadProps = {
    multiple: true,
    showUploadList: false,
    accept: "image/*",
    beforeUpload: (file) => handleFileUpload(file, "image/*"),
  };

  const videoUploadProps: UploadProps = {
    multiple: true,
    showUploadList: false,
    accept: "video/*",
    beforeUpload: (file) => handleFileUpload(file, "video/*"),
  };

  return (
    <>
      {/* {activeConversation && (
        <div className={styles.typingIndicator}>
          <span className={styles.typingText}>
            {activeConversation.name} đang soạn tin nhắn
            <span className={styles.typingDots}>
              <span className={styles.dot}></span>
              <span className={styles.dot}></span>
              <span className={styles.dot}></span>
            </span>
          </span>
        </div>
      )} */}
      <div className={styles.chatInput}>
        <Form form={form} onFinish={handleFinish}>
          <div className={styles.toolbar}>
            {!isZaloConversation && (
              <Upload {...fileUploadProps} disabled={disabled || !pageId}>
                <Tooltip title="Đính kèm tệp">
                  <Button
                    icon={<PaperIcon />}
                    type="text"
                    disabled={disabled}
                    loading={uploadingFiles.some((f) => f.type === "file")}
                  />
                </Tooltip>
              </Upload>
            )}
            <Upload
              {...imageUploadProps}
              disabled={disabled || (!pageId && !isZaloConversation)}
            >
              <Tooltip title="Hình ảnh">
                <Button
                  icon={<ImagePlusIcon />}
                  type="text"
                  disabled={disabled}
                  loading={uploadingFiles.some((f) => f.type === "image")}
                />
              </Tooltip>
            </Upload>
            {!isZaloConversation && (
              <Upload {...videoUploadProps} disabled={disabled || !pageId}>
                <Tooltip title="Video">
                  <Button
                    icon={<VideoIcon />}
                    type="text"
                    disabled={disabled}
                    loading={uploadingFiles.some((f) => f.type === "video")}
                  />
                </Tooltip>
              </Upload>
            )}
            <EmojiPicker onEmojiSelect={handleEmojiSelect}>
              <Tooltip title="Emoji">
                <Button icon={<EmojiIcon />} type="text" disabled={disabled} />
              </Tooltip>
            </EmojiPicker>
            {!isZaloConversation && (
              <TemplatePicker
                onTemplateSelect={handleTemplateSelect}
                pageId={pageId || undefined}
                activeConversation={activeConversation}
              >
                <Tooltip title="Template">
                  <Button
                    icon={<TemplateIcon />}
                    type="text"
                    disabled={disabled}
                  />
                </Tooltip>
              </TemplatePicker>
            )}
          </div>
          <div className={styles.inputRow}>
            <Form.Item name="message" noStyle className={styles.inputWrapper}>
              <Input.TextArea
                ref={inputRef}
                placeholder="Nhập tin nhắn..."
                autoSize={{ minRows: 2, maxRows: 4 }}
                onKeyDown={handleKeyDown}
                disabled={disabled}
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              className={styles.sendButton}
              shape="circle"
              disabled={disabled || uploadingFiles.length > 0}
              loading={uploadingFiles.length > 0}
            />
          </div>
          {(pendingAttachments.length > 0 || uploadingFiles.length > 0) && (
            <div className={styles.attachmentPreview}>
              {uploadingFiles.map((uploadingFile) => (
                <AttachmentRenderer
                  key={uploadingFile.id}
                  type={uploadingFile.type}
                  fileUrl={uploadingFile.fileUrl}
                  fileName={uploadingFile.fileName}
                  isUploading={true}
                />
              ))}
              {pendingAttachments.map((attachment, index) => (
                <AttachmentRenderer
                  key={`attachment-${index}`}
                  type={attachment.type}
                  fileUrl={attachment.fileUrl}
                  fileName={attachment.fileName}
                  isUploading={false}
                  onRemove={() => handleRemoveAttachment(index)}
                />
              ))}
            </div>
          )}
        </Form>
      </div>
    </>
  );
};
